export type PlanRowKey = 'lesson_title' | 'learning_outcomes' | 'activities' | 'assessment_questions';

export interface PlanCell {
  row: PlanRowKey;
  day: number;
  content: string;
}

export interface PlanEntry {
  id?: string;
  subject?: { id: string; name: string };
  teacher?: { id: string; firstName?: string; lastName?: string; username?: string };
  forcePageBreak: boolean;
  cells: PlanCell[];
}

export interface WeeklyPlan {
  id?: string;
  class?: { id: string; name: string; major?: { id: string; name: string } | null };
  weekOrdinal: number;
  startDate: string;
  endDate: string;
  schoolName: string;
  departmentName: string;
  sectionHeadName?: string;
  status: 'draft' | 'open' | 'locked';
  entries: PlanEntry[];
}

export interface PlanEntryUi {
  entryId?: string;
  subjectId: string;
  subjectName: string;
  teacherId: string;
  forcePageBreak: boolean;
  grid: string[][];
}

export interface WeeklyPlanSettings {
  headOfSectionName: string;
  principalName: string;
}

export interface WeeklyPlanMeta {
  schoolName: string;
  departmentName: string;
  classId: string;
  className: string;
  weekOrdinal: number;
  startDate: string;
  endDate: string;
}

export interface PdfSubjectData {
  subjectName: string;
  teacherName: string;
  rows: string[][];
  forcePageBreak: boolean;
}

export const PLAN_DAYS = ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'];

export const PLAN_ROWS: { key: PlanRowKey; label: string }[] = [
  { key: 'lesson_title', label: 'عنوان الدرس' },
  { key: 'learning_outcomes', label: 'نواتج التعلم' },
  { key: 'activities', label: 'الأنشطة' },
  { key: 'assessment_questions', label: 'أسئلة التقييم' },
];

export const WEEK_ORDINALS = [
  'الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس',
  'السادس', 'السابع', 'الثامن', 'التاسع', 'العاشر',
  'الحادي عشر', 'الثاني عشر', 'الثالث عشر', 'الرابع عشر', 'الخامس عشر',
  'السادس عشر', 'السابع عشر', 'الثامن عشر', 'التاسع عشر', 'العشرون',
];

export const DEFAULT_SCHOOL = 'مدرسة الحكمة الخاصة - التلة';
export const DEFAULT_DEPARTMENT = 'قسم الأساسي بنات التلة';
export const MAX_WEEKS = 20;

export function emptyGrid(): string[][] {
  return PLAN_ROWS.map(() => PLAN_DAYS.map(() => ''));
}

export function weekLabel(ordinal: number): string {
  const idx = ordinal - 1;
  if (idx >= 0 && idx < WEEK_ORDINALS.length) {
    return 'الأسبوع ' + WEEK_ORDINALS[idx];
  }
  return 'الأسبوع ' + ordinal;
}

export function gridToCells(grid: string[][]): PlanCell[] {
  const cells: PlanCell[] = [];
  grid.forEach((row, rowIndex) => {
    row.forEach((content, dayIndex) => {
      cells.push({
        row: PLAN_ROWS[rowIndex].key,
        day: dayIndex,
        content: content || '',
      });
    });
  });
  return cells;
}

export function cellsToGrid(cells: PlanCell[]): string[][] {
  const grid = emptyGrid();
  for (const cell of cells || []) {
    const rowIndex = PLAN_ROWS.findIndex(row => row.key === cell.row);
    if (rowIndex >= 0 && cell.day >= 0 && cell.day < PLAN_DAYS.length) {
      grid[rowIndex][cell.day] = cell.content || '';
    }
  }
  return grid;
}

export function teacherDisplayName(teacher?: { firstName?: string; lastName?: string; username?: string; name?: string } | null): string {
  if (!teacher) {
    return '';
  }
  const fullName = `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim();
  return fullName || teacher.name?.trim() || teacher.username || '';
}

export function entryToUi(entry: PlanEntry): PlanEntryUi {
  return {
    entryId: entry.id,
    subjectId: entry.subject?.id || '',
    subjectName: entry.subject?.name || '',
    teacherId: entry.teacher?.id || '',
    forcePageBreak: !!entry.forcePageBreak,
    grid: cellsToGrid(entry.cells || []),
  };
}

export function uiToEntry(entry: PlanEntryUi): { subject: string; teacher?: string; forcePageBreak: boolean; cells: PlanCell[] } {
  return {
    subject: entry.subjectId,
    teacher: entry.teacherId || undefined,
    forcePageBreak: entry.forcePageBreak,
    cells: gridToCells(entry.grid),
  };
}

export function mergeEntriesWithDefaults(
  savedEntries: PlanEntryUi[],
  defaults: PlanEntryUi[]
): PlanEntryUi[] {
  const savedBySubject = new Map(savedEntries.map(entry => [entry.subjectId, entry]));
  return defaults.map(defaultEntry => {
    const saved = savedBySubject.get(defaultEntry.subjectId);
    if (!saved) {
      return defaultEntry;
    }
    return {
      ...defaultEntry,
      ...saved,
      subjectId: defaultEntry.subjectId,
      subjectName: defaultEntry.subjectName,
      grid: saved.grid,
    };
  });
}
